export const faqIntro = [
  "The previous Sash Bag website is now offline as we move through a major operational transition. But Sash is not going away.",
  "There's a lot happening behind the scenes as the new team takes over operations, works through outstanding commitments, and prepares for the next version of Sash.",
  "Please read through the FAQ below before reaching out to Customer Service. We promise we aren't trying to hide anything — we're trying to make sure you have the information we currently have while our small team works through this transition.",
  "Thank you for your patience, your support, and for sticking with Sash through a very unusual chapter.",
];

export const faqCategories = [
  {
    title: "The Website & The Transition",
    items: [
      {
        q: "Why is the website offline?",
        a: [
          "The previous Sash Bag website has officially been taken offline as part of a major restructuring that has been taking place behind the scenes for quite some time.",
          "This isn't simply a website redesign or a change to the homepage. The previous website, inventory structure, and operational structure have come to an end as Sash moves into its next chapter.",
          "During this transition, a new operational team is taking over the day-to-day infrastructure of the business, including production, inventory, fulfillment, and other operational responsibilities.",
        ],
      },
      {
        q: "Is Sash going away?",
        a: [
          "NO! Sash is NOT going away. The way Sash operates is changing, but the brand itself is continuing.",
          "There will be a transition period while the new operational team and resources are put into place. The shopping website will remain offline during this process, and we don't yet have an exact date for when the new Sash website will launch.",
        ],
      },
      {
        q: "Is Nichole still involved?",
        a: [
          "YES. Very much so. Nichole has already begun meeting with the new team and will continue to be heavily involved throughout the transition.",
          "The biggest change is that she'll no longer be personally responsible for carrying the operational side of the business.",
          "The goal of this new structure is to put the operational resources, systems, and team in place that Sash needs while allowing Nichole to focus much more heavily on the parts of Sash she does best: the product, brand, community, creative direction, and vision for what comes next.",
        ],
      },
    ],
  },
  {
    title: "What Happens To My Order?",
    items: [
      {
        q: "Will Customer Service still be available while the website is offline?",
        a: [
          "YES! Customer Service is still functioning normally.",
          "You can continue to reach us at info@thesashbag.com regarding existing orders, outstanding commitments, shipping questions, or anything else you need help with.",
          "The website being offline does not mean Customer Service has disappeared.",
        ],
      },
      {
        q: "What about outstanding orders?",
        a: [
          "We know many of you have been waiting, and we have absolutely not forgotten about you. There are outstanding orders and customer commitments that still need to be fulfilled, and those are an important part of the work being addressed during this transition.",
          "The new operational team will be working with Nichole and the existing Sash team on production planning, inventory, fulfillment, and the handoff of these outstanding projects.",
          "There's a lot to organize, and we appreciate your patience while those plans are finalized.",
        ],
      },
    ],
  },
  {
    title: "Canadian Orders",
    items: [
      {
        q: "What's happening with the Toronto fulfillment center orders?",
        a: [
          "The inventory that had been sitting in the Toronto fulfillment center has begun moving again. Backorders are expected to ship during the September 1–7 timeframe.",
          "If you have a Canadian order that has been waiting, please keep an eye on your email for shipping and tracking information.",
        ],
      },
      {
        q: "Are Canadian orders still being handled during the transition?",
        a: [
          "Yes. Existing Canadian orders and customer commitments are still part of the fulfillment work being addressed during the transition.",
          "If you have questions about a specific order, please contact Customer Service at info@thesashbag.com.",
        ],
      },
    ],
  },
  {
    title: "Pre-Orders & Special Projects",
    items: [
      {
        q: "What about my pre-order?",
        a: [
          "We know many customers have been waiting on special projects and pre-orders, and you have NOT been forgotten. Some of the projects affected by the delays include:",
          { list: ["Phoenix Rising Leather", "Kickstarter Design Your Own Bag", "Nylon", "Black Leather Collection", "Ikat Half Sashes", "Remaining Paisley Bags", "Longer Straps"] },
          "These projects had to be put on hold while the larger restructuring of Sash was being worked through. Before production could move forward responsibly, the long-term operational structure of the company needed to be resolved. That's what this transition is designed to accomplish.",
          "The new team and Nichole will be doing production planning and working through the handoff of these outstanding projects. We know it has taken much longer than anyone wanted, and we appreciate how patient many of you have been.",
        ],
      },
      {
        q: "Will my pre-order still be fulfilled even though the old website is gone?",
        a: [
          "The website being offline does not mean outstanding customer commitments have disappeared. These projects remain part of the work being addressed during the transition.",
          "As production plans, timelines, and next steps are finalized, we'll continue sharing updates.",
        ],
      },
    ],
  },
  {
    title: "Kickstarter",
    items: [
      {
        q: "What about my Kickstarter Design Your Own Bag?",
        a: [
          "We know Kickstarter customers have been waiting a long time, and we know this has been frustrating. You have not been forgotten.",
          "The Kickstarter Design Your Own Bag project was one of the projects affected by the restructuring and had to be put on hold while the long-term future and operating structure of Sash were resolved.",
          "The project remains part of the outstanding work being addressed during this transition. The new operational team and Nichole will be reviewing production planning and determining the path forward for these bags as part of the larger handoff.",
          "As more concrete information becomes available, we'll share it. We truly appreciate everyone who has stuck with us through this process.",
        ],
      },
    ],
  },
  {
    title: "Rewards Points",
    items: [
      {
        q: "What happens to my Sash Rewards?",
        a: [
          "Your Sash Rewards are not being intentionally erased or abandoned because the previous website went offline.",
          "Due to system changes associated with the transition, we currently don't have full access to the previous Rewards back end. Part of the transition process will include determining how existing rewards are handled within the new website and operational system.",
          "We'll share more information as soon as we have it. If you have an old rewards email or reward code, we recommend holding onto it for your records.",
        ],
      },
    ],
  },
  {
    title: "Refunds",
    items: [
      {
        q: "What about refunds?",
        a: [
          "Outstanding refunds are still being worked through, and that work will continue during the transition. We know waiting is frustrating, particularly for customers who have already been waiting longer than expected.",
          "If you are waiting on a refund, your request has not disappeared because the website went offline. Please continue to contact Customer Service at info@thesashbag.com if you need help regarding a specific refund.",
        ],
      },
    ],
  },
  {
    title: "What Happens Next?",
    items: [
      {
        q: "When will the new website launch?",
        a: [
          "We don't have an exact date yet. There will be a transition period while the new operational structure, inventory, production plans, systems, and website are put into place.",
          "Our goal is for Sash to return in Q4, but we don't have a specific launch date to announce yet. We'd much rather give you accurate information when we have it than continually promise dates before all of the pieces are in place.",
        ],
      },
      {
        q: "What will the new Sash look like?",
        a: [
          "Some things will change. Some things absolutely won't.",
          "The operational structure behind Sash will be very different. The goal is to create better systems around inventory, production, fulfillment, and the day-to-day running of the company.",
          "But the heart of Sash isn't changing. Nichole will continue to be involved in the brand, products, community, and creative direction, and the Sash Sisters community remains an incredibly important part of what comes next.",
        ],
      },
      {
        q: "How long will this transition take?",
        a: [
          "We don't know exactly. There are a lot of moving pieces to hand over, including inventory, production planning, outstanding orders and projects, technology, fulfillment, and operational systems.",
          "The first meetings with the new team have already begun, and the transition is actively underway. As soon as we have concrete information to share about production, outstanding projects, or the new website, we'll share it.",
        ],
      },
    ],
  },
  {
    title: "Most Importantly…",
    items: [
      {
        q: "Did Sash forget about its customers?",
        a: [
          "Absolutely not. We know many of you have been incredibly patient through delays, shipping problems, production issues, restructuring, and everything else that has happened. We also understand that some of you are frustrated. That's fair.",
          "The purpose of this transition is not to walk away from those problems. It's to finally put a structure around Sash that gives the brand the resources and operational support it needs to address them and move forward.",
          "Your orders matter. Your pre-orders matter. Your Kickstarter commitments matter. And this community matters.",
          "This has been one of the strangest, hardest, and most transformational chapters in Sash history. But it isn't the end of Sash. It's the end of one version of Sash — and the beginning of what comes next.",
          "Thank you for being here for it. — The Sash Bag Team",
        ],
      },
    ],
  },
];
